import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faMedal,
  faRocket,
  faFire,
  faPeopleGroup,
  faCrown,
  faAward,
  faScaleBalanced,
  faLock,
  faStar,
  faClock,
  faCalendarCheck,
  faLightbulb,
  faDumbbell,
  faChartLine,
  faHeart,
  faPenToSquare,
  faArrowsRotate,
  faUserPlus,
  faArrowTrendUp,
  faGraduationCap,
} from '@fortawesome/free-solid-svg-icons';
import './BadgeShelf.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

export const BADGE_ICON = {
  gold: faTrophy,
  silver: faMedal,
  bronze: faMedal,
  challenger: faRocket,
  streak: faFire,
  teamwin: faPeopleGroup,
  bestteam: faCrown,
  coach: faAward,
  judge: faScaleBalanced,
  // badges the admin gives (icon names from backend/api/Awards/Awards.js)
  star: faStar,
  clock: faClock,
  calendar: faCalendarCheck,
  bulb: faLightbulb,
  dumbbell: faDumbbell,
  chart: faChartLine,
  heart: faHeart,
  pen: faPenToSquare,
  repeat: faArrowsRotate,
  fire: faFire,
  userplus: faUserPlus,
  levelup: faArrowTrendUp,
  grad: faGraduationCap, // "completed the course" posts on the wall
};
const iconOf = (b) => BADGE_ICON[b.icon] || BADGE_ICON[b.key] || faAward;

/* Badges & medals earned (and the ones still to earn, greyed, with how to get them).
   path: '/api/student/competitions/badges', '/api/teacher/competitions/badges' or an admin path.
   compact: only the earned ones, as small chips (dashboards, profiles); nothing when none. */
const BadgeShelf = ({ path, title = 'My badges & medals', compact = false, intro }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}${path}`, auth())
      .then((res) => !cancelled && setData(res.data))
      .catch(() => !cancelled && setData({ badges: [] }));
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (!data) return compact ? null : <div className="rmbs-skel" aria-hidden="true" />;
  const badges = data.badges || [];
  const earned = badges.filter((b) => b.earned);

  if (compact) {
    if (!earned.length) return null;
    return (
      <ul className="rmbs-chips" aria-label={title}>
        {earned.map((b) => (
          <li key={b.key} className={`is-${b.key}`} title={`${b.title}: ${b.how}${b.items?.length ? `\n${b.items.join('\n')}` : ''}`}>
            <FontAwesomeIcon icon={iconOf(b)} />
            <span>{b.title}</span>
            {b.count > 1 && <b>×{b.count}</b>}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section className="rmbs" aria-labelledby={`rmbs-${path.length}`}>
      <div className="rmbs-head">
        <h2 id={`rmbs-${path.length}`}>
          <FontAwesomeIcon icon={faTrophy} /> {title}
        </h2>
        <span>
          {earned.length} of {badges.length} earned
          {data.team ? ` · Team ${data.team}` : ''}
        </span>
      </div>
      {intro && <p className="rmbs-intro">{intro}</p>}
      <ul className="rmbs-grid">
        {badges.map((b) => (
          <li key={b.key} className={`rmbs-badge is-${b.key} ${b.earned ? 'is-earned' : 'is-locked'}`}>
            <span className="rmbs-icon" aria-hidden="true">
              <FontAwesomeIcon icon={b.earned ? iconOf(b) : faLock} />
              {b.count > 1 && <b>×{b.count}</b>}
            </span>
            <strong>{b.title}</strong>
            <small>{b.earned ? b.items?.[b.items.length - 1] || b.how : b.progress ? `${b.how} · ${b.progress}` : b.how}</small>
            {b.earned && b.items?.length > 1 && (
              <details>
                <summary>See all {b.items.length}</summary>
                <ul>
                  {b.items.map((it, i) => (
                    <li key={i}>{it}</li>
                  ))}
                </ul>
              </details>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default BadgeShelf;
